import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PdfFont,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { OptsImage, ImageTuple } from '../optsImage';
import { IItemCount } from '@educraft/base';
import { GUtil } from '../../pdf/gutil';
import { drawDitherLogo } from '../../pdf/ditherLogo';
import '../../pdf/draw/primitives';
import '../../pdf/draw/image';
import '../../pdf/draw/cut';
import '../../pdf/draw/string';

/**
 * Cerny_Petr - Old Maid / Black Peter card game
 *
 * Landscape pages:
 * - Front pages (0 to frontPageCount-1): Card fronts (4x2 grid), each column has same item top+bottom
 *   Last column(s) on last front page are "Black Peter" if itemCount % 4 != 0
 * - Black Peter page (if itemCount % 4 == 0): Full page of 8 Black Peter cards
 * - Back page (last): DitherLogo "PETR" card backs (golden color)
 *
 * Migrated from C# EduFrame.Img10.Generators.Cerny_Petr
 */
export class CernyPetr implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 3;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const itemCount = opts.list.length;
    const cardsPerPage = 4;
    const frontPageCount = Math.ceil(itemCount / cardsPerPage);
    const needsBlackPeterPage = itemCount % 4 === 0;
    const backPageIndex = needsBlackPeterPage ? frontPageCount + 1 : frontPageCount;
    const totalPages = backPageIndex + 1;

    const pages: PageGen[] = [];
    for (let i = 0; i < totalPages; i++) {
      pages.push(pdf.addPage(true));
    }

    const seed = opts.data.random.next();
    const backColor = PdfColor.fromRgb(200, 160, 0);

    const cardW = PdfUnit.FromMillimeters(57);
    const cardH = PdfUnit.FromMillimeters(87);
    const originX = (PdfSize.A4H.width - 4 * cardW) / 2;
    const originY = (PdfSize.A4H.height - 2 * cardH) / 2;

    const labels = opts.list.map((item) => item.label);
    const f = pages[0].adjustFont(cardW, labels, GUtil.baseFont);

    const drawBlackPeterCard = (page: PageGen, rect: PdfRect) => {
      const h = PdfUnit.FromMillimeters(10);
      const mx = PdfUnit.FromMillimeters(5);
      const my = PdfUnit.FromMillimeters(8);

      const r1 = new PdfRect(rect.x + mx, rect.y + my, rect.width - 2 * mx, h);
      const r2 = new PdfRect(rect.x + mx, rect.y + rect.height - my - h, rect.width - 2 * mx, h);
      const peterDotSize = PdfUnit.FromMillimeters(6);
      const bigDotSize = PdfUnit.FromMillimeters(35);

      function drawDot(p: PageGen, px: number, py: number, sz: number) {
        p.drawEllipse(null, PdfBrush.Black, new PdfRect(px - sz / 2, py - sz / 2, sz, sz));
      }

      drawDot(page, r1.x, r1.y, peterDotSize);
      drawDot(page, r1.right, r1.y, peterDotSize);
      drawDot(page, r2.x, r2.y + r2.height, peterDotSize);
      drawDot(page, r2.right, r2.y + r2.height, peterDotSize);

      const cx = rect.x + rect.width / 2;
      const cy = rect.y + rect.height / 2;
      const peterRect = new PdfRect(
        cx - bigDotSize / 2,
        cy - bigDotSize / 2,
        bigDotSize,
        bigDotSize
      );
      page.drawEllipse(null, PdfBrush.Black, peterRect);
      page.drawString(
        opts.data.lang.Cards_PETR ?? 'PETR',
        GUtil.baseFont,
        new PdfBrush(PdfColor.fromRgb(255, 255, 255)),
        null,
        peterRect,
        ETextAlign.Center
      );
    };

    const drawFrontCard = (page: PageGen, rect: PdfRect, item: ImageTuple) => {
      const imgRect = new PdfRect(
        rect.x,
        rect.y + (rect.height - rect.width) / 2,
        rect.width,
        rect.width
      );
      const topRect = new PdfRect(rect.x, rect.y, rect.width, imgRect.y - rect.y);
      const bottomRect = new PdfRect(
        rect.x,
        imgRect.y + imgRect.height,
        rect.width,
        topRect.height
      );

      if (item.label) {
        page.drawString(item.label, f, PdfBrush.Black, null, topRect, ETextAlign.Center);
      }
      page.drawString(item.label, f, PdfBrush.Black, null, bottomRect, ETextAlign.Center);
      page.drawImageCenter(item.image, imgRect);
    };

    for (let pageIdx = 0; pageIdx < frontPageCount; pageIdx++) {
      const page = pages[pageIdx];
      const origin = new PdfPoint(originX, originY);
      page.cutCross(origin);
      page.cutCross(new PdfPoint(originX + cardW * 4, originY + cardH * 2));

      for (let y = 0; y < 2; y++) {
        for (let x = 0; x < 4; x++) {
          const rect = new PdfRect(originX + x * cardW, originY + y * cardH, cardW, cardH);
          page.drawRectangle(GUtil.cutPen, null, rect);

          const cardIndex = pageIdx * 4 + x;

          if (cardIndex < itemCount) {
            drawFrontCard(page, rect, opts.list[cardIndex]);
          } else {
            drawBlackPeterCard(page, rect);
          }
        }
      }
    }

    if (needsBlackPeterPage) {
      const bpPage = pages[frontPageCount];
      bpPage.cutCross(new PdfPoint(originX, originY));
      bpPage.cutCross(new PdfPoint(originX + cardW * 4, originY + cardH * 2));

      for (let y = 0; y < 2; y++) {
        for (let x = 0; x < 4; x++) {
          const rect = new PdfRect(originX + x * cardW, originY + y * cardH, cardW, cardH);
          bpPage.drawRectangle(GUtil.cutPen, null, rect);
          drawBlackPeterCard(bpPage, rect);
        }
      }
    }

    const backPage = pages[backPageIndex];
    backPage.cutCross(new PdfPoint(originX, originY));
    backPage.cutCross(new PdfPoint(originX + cardW * 4, originY + cardH * 2));

    for (let y = 0; y < 2; y++) {
      for (let x = 0; x < 4; x++) {
        const rect = new PdfRect(originX + x * cardW, originY + y * cardH, cardW, cardH);
        backPage.drawRectangle(GUtil.cutPen, null, rect);
        await drawDitherLogo(
          backPage,
          opts.data.lang.Cards_PETR ?? 'PETR',
          backColor,
          rect,
          seed
        );
      }
    }
  }
}
